import { ComponentProps, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../contexts/App";
import { useSite } from "../../../contexts/Site";
import { AI_FIELDS, AiFieldKey } from "../../../model/aiFields";
import { categoryLabel } from "../../../model/categories";
import { requestAiSuggestion } from "../../../services/aiSuggestions";
import { AiSuggestionLanguage, hasActiveSubscription } from "../../../services/api";
import { PlusOnlyModal } from "../../PlusOnlyModal";
import { TextArea } from "../Input/textArea";
import { Actions, AiButton, Container, ErrorText } from "./styles";

// The panel only comes in Portuguese and English: i18n may report "pt-BR", "en-US"...
const panelLanguage = (language: string): AiSuggestionLanguage =>
  language.toLowerCase().startsWith("pt") ? "pt" : "en";

// Longer than this, the request is given up and the user can try again
const TIMEOUT = 20000;

type Props = Omit<ComponentProps<typeof TextArea>, "onChange"> & {
  value?: string;
  onChange: (value: string) => void;
  /** Which field this is (see model/aiFields): sets the AI's guidance and length limit. */
  aiField: AiFieldKey;
  /** Extra context for the AI besides the Keepo's title and category (e.g. the item's title). */
  aiContext?: Record<string, string | undefined>;
};

const SparkleIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l2.2 6.3L20.5 10l-6.3 2.2L12 18.5l-2.2-6.3L3.5 10l6.3-1.7L12 2zM19 15l.9 2.6 2.6.9-2.6.9L19 22l-.9-2.6-2.6-.9 2.6-.9L19 15z" />
  </svg>
);

/**
 * Long text field with a "Sugerir com IA" button inside. PLUS only: on a FREE Keepo the button opens
 * the PLUS notice. The suggestion replaces the field's text; "Desfazer" brings the previous one back.
 */
export const AiTextArea = ({ value = "", onChange, aiField, aiContext, ...textAreaProps }: Props) => {
  const { t, i18n } = useTranslation();
  const { site } = useSite();
  const { appState } = useAppState();

  const [isLoading, setLoading] = useState(false);
  const [hasError, setError] = useState(false);
  const [previousValue, setPreviousValue] = useState<string>();
  const [hasSuggested, setSuggested] = useState(false);
  const [isShowingPlusModal, setShowingPlusModal] = useState(false);
  const abortRef = useRef<AbortController>();
  // Set by "Cancelar": an abort that isn't a failure (a timeout abort is)
  const cancelledRef = useRef(false);

  useEffect(() => () => abortRef.current?.abort(), []);

  const isPlus = hasActiveSubscription(appState.editingSiteSubscription);
  const prefix = "components.aiTextArea";

  const suggest = async () => {
    if (!isPlus) {
      setShowingPlusModal(true);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    cancelledRef.current = false;
    const timer = setTimeout(() => controller.abort(), TIMEOUT);
    setLoading(true);
    setError(false);
    try {
      const { maxLength } = AI_FIELDS[aiField];
      const text = await requestAiSuggestion(
        appState.editingSiteId as string,
        {
          field: aiField,
          instruction: t(`${prefix}.instructions.${aiField}`),
          maxLength,
          language: panelLanguage(i18n.language),
          context: {
            siteTitle: site?.title,
            category: categoryLabel(site?.category, site?.categoryOther, t),
            currentValue: value || undefined,
            ...aiContext,
          },
        },
        controller.signal
      );
      const finalText = text.slice(0, maxLength);
      setPreviousValue(value);
      onChange(finalText);
      setSuggested(true);
    } catch {
      setError(!cancelledRef.current);
    } finally {
      clearTimeout(timer);
      abortRef.current = undefined;
      setLoading(false);
    }
  };

  const cancel = () => {
    cancelledRef.current = true;
    abortRef.current?.abort();
  };

  const undo = () => {
    onChange(previousValue || "");
    setPreviousValue(undefined);
    setSuggested(false);
  };

  return (
    <Container $loading={isLoading}>
      <div className="field" aria-busy={isLoading}>
        <TextArea
          {...textAreaProps}
          value={value}
          readOnly={isLoading}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        />
        <Actions>
          {isLoading ? (
            <button type="button" className="cancel" onClick={cancel}>
              {t(`${prefix}.cancel`)}
            </button>
          ) : (
            previousValue !== undefined && (
              <button type="button" className="undo" onClick={undo}>
                {t(`${prefix}.undo`)}
              </button>
            )
          )}
          <AiButton type="button" disabled={isLoading} onClick={suggest}>
            {isLoading ? <span className="spinner" aria-hidden="true" /> : <SparkleIcon />}
            {isLoading ? t(`${prefix}.loading`) : hasSuggested ? t(`${prefix}.again`) : t(`${prefix}.suggest`)}
          </AiButton>
        </Actions>
      </div>
      {hasError && <ErrorText role="alert">{t(`${prefix}.error`)}</ErrorText>}
      {isShowingPlusModal && (
        <PlusOnlyModal
          feature={t(`${prefix}.feature`)}
          description={t(`${prefix}.plusDescription`)}
          onClose={() => setShowingPlusModal(false)}
        />
      )}
    </Container>
  );
};
